import React, { useState, useEffect } from 'react';
import { 
  FiUpload, FiImage, FiTrash2, FiEdit2, FiX, FiCalendar, 
  FiUsers, FiPlus, FiCheck, FiClock, FiHome, FiUser,
  FiSettings, FiLogOut, FiLock, FiFilm, FiPackage, FiStar
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { 
  authAPI, bookingsAPI, servicesAPI, availabilityAPI, 
  photosAPI, videosAPI, contentAPI, galleryAPI, packagesAPI, addonsAPI, uploadAPI 
} from '../utils/api';
import './Admin.css';

const Admin = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState('photos');
  
  // Data states
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [aboutContent, setAboutContent] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [sessionPackages, setSessionPackages] = useState([]);
  const [bookingAddons, setBookingAddons] = useState([]);
  
  // UI states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [draggedPhotoId, setDraggedPhotoId] = useState(null);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    setCheckingAuth(true);
    try {
      if (authAPI.isLoggedIn()) {
        const result = await authAPI.check();
        setIsAuthenticated(result.authenticated);
        if (result.authenticated) {
          loadAllData();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
    setCheckingAuth(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await authAPI.login(loginForm.email, loginForm.password);
      setIsAuthenticated(true);
      loadAllData();
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.message || 'Login failed');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    authAPI.logout();
    setIsAuthenticated(false);
    toast.success('Logged out');
  };

  // Load all data
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [photosRes, videosRes, servicesRes, bookingsRes, contentRes, galleryRes, packagesRes, addonsRes] = await Promise.all([
        photosAPI.getAll().catch(() => ({ photos: [] })),
        videosAPI.getAll().catch(() => ({ videos: [] })),
        servicesAPI.getAll().catch(() => ({ services: [] })),
        bookingsAPI.getAll().catch(() => ({ bookings: [] })),
        contentAPI.getAll().catch(() => ({ content: {} })),
        galleryAPI.getAll().catch(() => ({ items: [] })),
        packagesAPI.getAll().catch(() => ({ packages: [] })),
        addonsAPI.getAll().catch(() => ({ addons: [] }))
      ]);

      setPhotos(photosRes.photos || []);
      setVideos(videosRes.videos || []);
      setServices(servicesRes.services || []);
      setBookings(bookingsRes.bookings || []);
      setGalleryItems(galleryRes.items || []);
      setSessionPackages(packagesRes.packages || []);
      setBookingAddons(addonsRes.addons || []);
      
      if (contentRes.content) {
        setHomeContent(contentRes.content.home || null);
        setAboutContent(contentRes.content.about || null);
        setSettings(contentRes.content.settings || null);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load some data');
    }
    setLoading(false);
  };

  // Load availability when month changes
  useEffect(() => {
    if (isAuthenticated) {
      loadAvailability();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, isAuthenticated]);

  const loadAvailability = async () => {
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      const res = await availabilityAPI.getAll(month, year);
      setAvailability(res.availability || []);
    } catch (error) {
      console.error('Failed to load availability:', error);
    }
  };

  // Compress image helper
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Photo upload
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const base64Url = await compressImage(file);
        
        const result = await photosAPI.upload(base64Url, {
          title: file.name.replace(/\.[^/.]+$/, ''),
          category: 'other',
          isPortfolio: true
        });
        
        if (result.photo) {
          setPhotos(prev => [...prev, result.photo]);
        }
      }
      toast.success('Photos uploaded successfully!');
    } catch (error) {
      toast.error('Error uploading photos');
      console.error(error);
    }
    setUploading(false);
    e.target.value = '';
  };

  // Video add (URL based)
  const handleAddVideo = async (videoData) => {
    try {
      const result = await videosAPI.create(videoData);
      if (result.video) {
        setVideos(prev => [...prev, result.video]);
        toast.success('Video added!');
      }
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  // Delete handlers
  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await photosAPI.delete(id);
      setPhotos(prev => prev.filter(p => p._id !== id));
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Photo drag and drop reordering
  const handleDragStart = (e, photoId) => {
    setDraggedPhotoId(photoId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetPhotoId) => {
    e.preventDefault();
    if (!draggedPhotoId || draggedPhotoId === targetPhotoId) {
      setDraggedPhotoId(null);
      return;
    }

    const draggedIndex = photos.findIndex(p => p._id === draggedPhotoId);
    const targetIndex = photos.findIndex(p => p._id === targetPhotoId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedPhotoId(null);
      return;
    }

    // Reorder locally first for instant feedback
    const newPhotos = [...photos];
    const [draggedPhoto] = newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(targetIndex, 0, draggedPhoto);
    setPhotos(newPhotos);
    setDraggedPhotoId(null);

    // Save new order to database: portfolio photos get 0,1,2,... so Portfolio page matches admin order
    try {
      const portfolioPhotos = newPhotos.filter((p) => p.isPortfolio !== false);
      const nonPortfolioPhotos = newPhotos.filter((p) => p.isPortfolio === false);
      const photoOrders = [
        ...portfolioPhotos.map((photo, index) => ({ id: photo._id, order: index })),
        ...nonPortfolioPhotos.map((photo, index) => ({ id: photo._id, order: 10000 + index }))
      ];
      await photosAPI.reorder(photoOrders);
      toast.success('Photo order updated');
    } catch (error) {
      console.error('Failed to save order:', error);
      toast.error('Failed to save order');
      // Reload photos to get correct order
      const res = await photosAPI.getAll();
      setPhotos(res.photos || []);
    }
  };

  const handleDragEnd = () => {
    setDraggedPhotoId(null);
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await videosAPI.delete(id);
      setVideos(prev => prev.filter(v => v._id !== id));
      toast.success('Video deleted');
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  // Edit photo/video
  const handleSaveEdit = async () => {
    if (!editingItem) return;
    try {
      if (editingItem.type === 'photo') {
        const { type, ...updates } = editingItem;
        await photosAPI.update(editingItem._id, updates);
        setPhotos(prev => prev.map(p => p._id === editingItem._id ? { ...p, ...updates } : p));
      } else {
        const { type, ...updates } = editingItem;
        await videosAPI.update(editingItem._id, updates);
        setVideos(prev => prev.map(v => v._id === editingItem._id ? { ...v, ...updates } : v));
      }
      setEditingItem(null);
      toast.success('Saved!');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  // Service handlers
  const handleSaveService = async () => {
    if (!editingService?.name || !editingService?.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      if (editingService.isNew) {
        const result = await servicesAPI.create(editingService);
        setServices(prev => [...prev, result.service]);
      } else {
        await servicesAPI.update(editingService._id, editingService);
        setServices(prev => prev.map(s => s._id === editingService._id ? editingService : s));
      }
      setEditingService(null);
      toast.success('Service saved!');
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await servicesAPI.delete(id);
      setServices(prev => prev.filter(s => s._id !== id));
      toast.success('Service deleted');
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Availability handlers
  const toggleDateAvailability = async (dateStr) => {
    const existing = availability.find(a => a.date === dateStr);
    const newAvailable = existing ? !existing.available : false;
    
    try {
      await availabilityAPI.update(dateStr, newAvailable);
      
      if (existing) {
        setAvailability(prev => prev.map(a => 
          a.date === dateStr ? { ...a, available: newAvailable } : a
        ));
      } else {
        setAvailability(prev => [...prev, { date: dateStr, available: false }]);
      }
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const isDateBlocked = (dateStr) => {
    const item = availability.find(a => a.date === dateStr);
    return item && !item.available;
  };

  // Booking handlers
  const handleUpdateBookingStatus = async (id, status) => {
    try {
      await bookingsAPI.update(id, { status });
      setBookings(prev => prev.map(b => b._id === id ? { ...b, status } : b));
      toast.success(`Booking ${status}!`);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await bookingsAPI.delete(id);
      setBookings(prev => prev.filter(b => b._id !== id));
      toast.success('Booking deleted');
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  // Content handlers
  const handleSaveContent = async (section, data) => {
    try {
      await contentAPI.update(section, data);
      if (section === 'home') setHomeContent(data);
      if (section === 'about') setAboutContent(data);
      if (section === 'settings') setSettings(data);
      setEditingContent(null);
      toast.success('Content saved!');
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  // Gallery item handlers
  const handleSaveGalleryItem = async () => {
    if (!editingGalleryItem?.title || !editingGalleryItem?.category) {
      toast.error('Title and category are required');
      return;
    }
    try {
      if (editingGalleryItem.isNew) {
        const result = await galleryAPI.create(editingGalleryItem);
        setGalleryItems(prev => [...prev, result.item]);
      } else {
        await galleryAPI.update(editingGalleryItem._id, editingGalleryItem);
        setGalleryItems(prev => prev.map(i => i._id === editingGalleryItem._id ? editingGalleryItem : i));
      }
      setEditingGalleryItem(null);
      toast.success('Gallery item saved!');
    } catch (error) {
      toast.error('Failed to save gallery item');
    }
  };

  const handleDeleteGalleryItem = async (id) => {
    if (!window.confirm('Delete this gallery item?')) return;
    try {
      await galleryAPI.delete(id);
      setGalleryItems(prev => prev.filter(i => i._id !== id));
      toast.success('Gallery item deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Session Package handlers
  const handleSavePackage = async () => {
    if (!editingPackage?.name || !editingPackage?.duration) {
      toast.error('Name and duration are required');
      return;
    }
    const toSave = { ...editingPackage };
    if (toSave.price === '' || toSave.price === undefined) toSave.price = null;
    else if (typeof toSave.price === 'string') toSave.price = toSave.price.trim() ? Number(toSave.price) : null;
    try {
      if (editingPackage.isNew) {
        const result = await packagesAPI.create(toSave);
        setSessionPackages(prev => [...prev, result.package]);
      } else {
        await packagesAPI.update(editingPackage._id, toSave);
        setSessionPackages(prev => prev.map(p => p._id === editingPackage._id ? { ...p, ...toSave } : p));
      }
      setEditingPackage(null);
      toast.success('Package saved!');
    } catch (error) {
      toast.error('Failed to save package');
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await packagesAPI.delete(id);
      setSessionPackages(prev => prev.filter(p => p._id !== id));
      toast.success('Package deleted');
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  // Booking Addon handlers
  const handleSaveAddon = async () => {
    if (!editingAddon?.name || !editingAddon?.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      if (editingAddon.isNew) {
        const result = await addonsAPI.create(editingAddon);
        setBookingAddons(prev => [...prev, result.addon]);
      } else {
        await addonsAPI.update(editingAddon._id, editingAddon);
        setBookingAddons(prev => prev.map(a => a._id === editingAddon._id ? editingAddon : a));
      }
      setEditingAddon(null);
      toast.success('Add-on saved!');
    } catch (error) {
      toast.error('Failed to save add-on');
    }
  };

  const handleDeleteAddon = async (id) => {
    if (!window.confirm('Delete this add-on?')) return;
    try {
      await addonsAPI.delete(id);
      setBookingAddons(prev => prev.filter(a => a._id !== id));
      toast.success('Add-on deleted');
    } catch (error) {
      toast.error('Failed to delete add-on');
    }
  };

  // Package feature helpers
  const addFeatureToPackage = () => {
    if (!editingPackage) return;
    const features = editingPackage.features || [];
    setEditingPackage({
      ...editingPackage,
      features: [...features, { text: '', included: true }]
    });
  };

  const updateFeature = (index, field, value) => {
    if (!editingPackage) return;
    const features = [...editingPackage.features];
    features[index] = { ...features[index], [field]: value };
    setEditingPackage({ ...editingPackage, features });
  };

  const removeFeature = (index) => {
    if (!editingPackage) return;
    const features = editingPackage.features.filter((_, i) => i !== index);
    setEditingPackage({ ...editingPackage, features });
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { daysInMonth: lastDay.getDate(), startingDay: firstDay.getDay() };
  };

  const formatDate = (date) => date.toISOString().split('T')[0];

  const getPositionValues = (position) => {
    if (!position) return { left: '50%', top: '50%' };
    const parts = position.split(' ');
    let left = '50%', top = '50%';
    if (parts[0] === 'left') left = '0%';
    else if (parts[0] === 'right') left = '100%';
    else if (parts[0] !== 'center') left = parts[0];
    if (parts[1] === 'top') top = '0%';
    else if (parts[1] === 'bottom') top = '100%';
    else if (parts[1] !== 'center') top = parts[1];
    return { left, top };
  };

  // Render availability calendar
  const renderAvailabilityCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(selectedMonth);
    const days = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];

    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="cal-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
      const dateStr = formatDate(date);
      const blocked = isDateBlocked(dateStr);
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

      days.push(
        <div
          key={day}
          className={`cal-day ${blocked ? 'blocked' : 'available'} ${isPast ? 'past' : ''}`}
          onClick={() => !isPast && toggleDateAvailability(dateStr)}
        >
          <span>{day}</span>
          {blocked && <FiX className="blocked-icon" />}
        </div>
      );
    }

    return (
      <div className="availability-calendar">
        <div className="cal-header">
          <button onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1))}>←</button>
          <h3>{monthNames[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}</h3>
          <button onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1))}>→</button>
        </div>
        <div className="cal-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="cal-days">{days}</div>
        <p className="cal-hint">Click a date to block/unblock. Red = blocked, Green = available</p>
      </div>
    );
  };

  // Login screen
  if (checkingAuth) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <div className="login-container">
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <div className="login-container">
            <div className="login-icon"><FiLock /></div>
            <h1>Admin Login</h1>
            <p>Enter your credentials to access the admin panel</p>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              <button type="submit" className="save-button" disabled={loginLoading}>
                {loginLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
            <p className="login-hint">Contact administrator for login credentials.</p>
          </div>
        </div>
      </div>
    );
  }

  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage your website content, portfolio, and bookings</p>
        <button className="logout-btn" onClick={handleLogout}>
          <FiLogOut /> Logout
        </button>
      </div>

      <div className="admin-tabs">
        <button className={`tab-button ${activeTab === 'photos' ? 'active' : ''}`} onClick={() => setActiveTab('photos')}>
          <FiImage /> Photos
        </button>
        {/* Videos tab hidden - use Reels tab instead
        <button className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`} onClick={() => setActiveTab('videos')}>
          <FiVideo /> Videos
        </button>
        */}
        {/* Gallery tab hidden for now
        <button className={`tab-button ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')}>
          <FiGrid /> Gallery
        </button>
        */}
        <button className={`tab-button ${activeTab === 'reels' ? 'active' : ''}`} onClick={() => setActiveTab('reels')}>
          <FiFilm /> Reels
        </button>
        {/* Services tab hidden - use Booking tab for session packages instead
        <button className={`tab-button ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>
          <FiDollarSign /> Services
        </button>
        */}
        <button className={`tab-button ${activeTab === 'booking-settings' ? 'active' : ''}`} onClick={() => setActiveTab('booking-settings')}>
          <FiPackage /> Booking
        </button>
        <button className={`tab-button ${activeTab === 'availability' ? 'active' : ''}`} onClick={() => setActiveTab('availability')}>
          <FiCalendar /> Availability
        </button>
        <button className={`tab-button ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>
          <FiUsers /> Bookings {pendingCount > 0 && <span className="badge">{pendingCount}</span>}
        </button>
        <button className={`tab-button ${activeTab === 'home' ? 'active' : ''}`} onClick={() => setActiveTab('home')}>
          <FiHome /> Home
        </button>
        <button className={`tab-button ${activeTab === 'about' ? 'active' : ''}`} onClick={() => setActiveTab('about')}>
          <FiUser /> About
        </button>
        <button className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          <FiSettings /> Settings
        </button>
      </div>

      <div className="admin-content">
        {loading && <div className="loading-state">Loading...</div>}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <>
            <div className="upload-section">
              <label className="upload-button">
                <FiUpload /> Upload Photos
                <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
              </label>
              {uploading && <p className="upload-status">Uploading...</p>}
              <p className="upload-hint">Upload photos, then click Edit to choose where they appear (Portfolio, Slider, Home page)</p>
            </div>
            
            {/* Quick stats */}
            <div className="photo-stats">
              <span className="stat-badge portfolio">Portfolio: {photos.filter(p => p.isPortfolio !== false).length}</span>
              <span className="stat-badge slider">Slider: {photos.filter(p => p.isSlider).length}</span>
              <span className="stat-badge home">Home Stories: {photos.filter(p => p.isHomePage).length}</span>
            </div>
            
            <p className="drag-hint">Drag and drop photos to reorder them. Numbers show Portfolio position (— = not on portfolio). The order will be reflected on the Portfolio page.</p>
            <p className="drag-hint-sub">Numbers show Portfolio order (— = not on Portfolio).</p>
            <div className="media-grid">
              {photos.map((item, index) => {
                const portfolioRank = item.isPortfolio !== false
                  ? 1 + photos.slice(0, index).filter(p => p.isPortfolio !== false).length
                  : null;
                return (
                <div 
                  key={item._id} 
                  className={`media-item draggable ${draggedPhotoId === item._id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item._id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, item._id)}
                  onDragEnd={handleDragEnd}
                >
                  <span className="photo-order">{portfolioRank ?? '—'}</span>
                  <img src={item.url} alt={item.title} style={{ objectPosition: item.position || 'center center' }} />
                  {/* Photo badges */}
                  <div className="photo-badges">
                    {item.isSlider && <span className="badge slider">Slider</span>}
                    {item.isHomePage && <span className="badge home">Home</span>}
                    {item.isPortfolio !== false && <span className="badge portfolio">Portfolio</span>}
                  </div>
                  <div className="media-overlay">
                    <div className="media-info">
                      <h3>{item.title || 'Untitled'}</h3>
                      <p>{item.category}</p>
                    </div>
                    <div className="media-actions">
                      <button onClick={() => setEditingItem({ ...item, type: 'photo' })} className="action-btn edit"><FiEdit2 /></button>
                      <button onClick={() => handleDeletePhoto(item._id)} className="action-btn delete"><FiTrash2 /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
            {photos.length === 0 && <div className="empty-state"><p>No photos uploaded yet.</p></div>}
          </>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <>
            <div className="upload-section">
              <button className="upload-button" onClick={() => setEditingItem({ type: 'video', isNew: true, title: '', url: '', category: 'reel' })}>
                <FiPlus /> Add Video URL
              </button>
              <p className="upload-hint">Add video URLs from Cloudinary or other sources</p>
            </div>
            <div className="media-grid">
              {videos.map((item) => (
                <div key={item._id} className="media-item">
                  <video src={item.url} muted poster={item.posterUrl} />
                  <div className="media-overlay">
                    <div className="media-info">
                      <h3>{item.title}</h3>
                      <p>{item.category}</p>
                    </div>
                    <div className="media-actions">
                      <button onClick={() => setEditingItem({ ...item, type: 'video' })} className="action-btn edit"><FiEdit2 /></button>
                      <button onClick={() => handleDeleteVideo(item._id)} className="action-btn delete"><FiTrash2 /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {videos.length === 0 && <div className="empty-state"><p>No videos added yet.</p></div>}
          </>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="services-tab">
            <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Gallery Categories</h2>
            <button className="add-service-btn" onClick={() => setEditingGalleryItem({ isNew: true, title: '', description: '', category: 'personal', images: [] })}>
              <FiPlus /> Add Gallery Item
            </button>
            <div className="services-list">
              {galleryItems.map(item => (
                <div key={item._id} className="service-item">
                  <div className="service-info">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="service-meta">
                      <span>{item.category}</span>
                      <span>{item.images?.length || 0} images</span>
                    </div>
                  </div>
                  <div className="service-actions">
                    <button onClick={() => setEditingGalleryItem(item)} className="action-btn edit"><FiEdit2 /></button>
                    <button onClick={() => handleDeleteGalleryItem(item._id)} className="action-btn delete"><FiTrash2 /></button>
                  </div>
                </div>
              ))}
            </div>
            {galleryItems.length === 0 && <div className="empty-state"><p>No gallery items yet.</p></div>}
          </div>
        )}

        {/* Reels Tab */}
        {activeTab === 'reels' && (
          <>
            <div className="upload-section">
              <label className="upload-button">
                <FiUpload /> Upload Reel Video
                <input 
                  type="file" 
                  accept="video/*" 
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (!file.type.startsWith('video/')) {
                      toast.error('Please select a video file');
                      return;
                    }
                    if (file.size > 500 * 1024 * 1024) {
                      toast.error('Video must be under 500MB');
                      return;
                    }
                    setUploading(true);
                    setUploadProgress(0);
                    try {
                      // Upload directly to Cloudinary (bypasses server timeout)
                      const uploadResult = await uploadAPI.uploadToCloudinary(
                        file, 
                        'reels', 
                        'video',
                        (progress) => setUploadProgress(progress)
                      );
                      
                      // Save video record to our database
                      const result = await videosAPI.create({
                        url: uploadResult.url,
                        cloudinaryId: uploadResult.publicId,
                        title: file.name.replace(/\.[^/.]+$/, ''),
                        category: 'reel',
                        isReel: true
                      });
                      
                      if (result.video) {
                        setVideos(prev => [...prev, result.video]);
                        toast.success('Reel uploaded successfully!');
                      }
                    } catch (error) {
                      console.error('Upload error:', error);
                      toast.error('Failed to upload video');
                    } finally {
                      setUploading(false);
                      setUploadProgress(0);
                    }
                    e.target.value = '';
                  }}
                  disabled={uploading}
                />
              </label>
              <button className="upload-button" style={{ marginLeft: '1rem' }} onClick={() => setEditingItem({ type: 'video', isNew: true, title: '', url: '', category: 'reel', isReel: true })}>
                <FiPlus /> Add URL
              </button>
              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <p className="upload-status">Uploading... {uploadProgress}%</p>
                </div>
              )}
              <p className="upload-hint">Upload video files (max 500MB) or add video URLs</p>
            </div>
            <div className="media-grid">
              {videos.filter(v => v.isReel).map((item) => (
                <div key={item._id} className="media-item">
                  <video src={item.url} muted poster={item.posterUrl} />
                  <div className="media-overlay">
                    <div className="media-info">
                      <h3>{item.title}</h3>
                    </div>
                    <div className="media-actions">
                      <button onClick={() => setEditingItem({ ...item, type: 'video' })} className="action-btn edit"><FiEdit2 /></button>
                      <button onClick={() => handleDeleteVideo(item._id)} className="action-btn delete"><FiTrash2 /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {videos.filter(v => v.isReel).length === 0 && <div className="empty-state"><p>No reels added yet.</p></div>}
          </>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="services-tab">
            <button className="add-service-btn" onClick={() => setEditingService({ isNew: true, name: '', duration: '', price: 0, description: '' })}>
              <FiPlus /> Add Service
            </button>
            <div className="services-list">
              {services.map(service => (
                <div key={service._id} className="service-item">
                  <div className="service-info">
                    <h3>{service.name}</h3>
                    <p>{service.description}</p>
                    <div className="service-meta">
                      <span><FiClock /> {service.duration}</span>
                      <span className="price">€{service.price}</span>
                    </div>
                  </div>
                  <div className="service-actions">
                    <button onClick={() => setEditingService(service)} className="action-btn edit"><FiEdit2 /></button>
                    <button onClick={() => handleDeleteService(service._id)} className="action-btn delete"><FiTrash2 /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Booking Settings Tab */}
        {activeTab === 'booking-settings' && (
          <div className="booking-settings-tab">
            <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>Booking Page Settings</h2>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '3rem' }}>
              Manage session packages and add-on services displayed on the booking page
            </p>

            {/* Session Packages Section */}
            <div className="booking-section">
              <div className="section-header">
                <h3><FiPackage /> Session Packages</h3>
                <button 
                  className="add-service-btn" 
                  onClick={() => setEditingPackage({ 
                    isNew: true, 
                    name: '', 
                    duration: '', 
                    price: null, 
                    studioRentalPrice: 0,
                    description: '', 
                    features: [],
                    recommended: false,
                    order: sessionPackages.length + 1
                  })}
                >
                  <FiPlus /> Add Package
                </button>
              </div>
              <div className="packages-list">
                {sessionPackages.sort((a, b) => a.order - b.order).map(pkg => (
                  <div key={pkg._id} className={`package-item ${pkg.recommended ? 'recommended' : ''}`}>
                    {pkg.recommended && <span className="recommended-tag"><FiStar /> Recommended</span>}
                    {pkg.discountPercent > 0 && <span className="discount-tag">-{pkg.discountPercent}%</span>}
                    <div className="package-info">
                      <h3>{pkg.name}</h3>
                      <p>{pkg.description}</p>
                      <div className="package-meta">
                        <span><FiClock /> {pkg.duration}</span>
                        {pkg.price != null && pkg.price > 0 ? (
                          pkg.discountPercent > 0 ? (
                            <span className="price">
                              <del style={{color: '#999', marginRight: '0.5rem'}}>{pkg.price?.toLocaleString()}</del>
                              {Math.round(pkg.price * (1 - pkg.discountPercent / 100)).toLocaleString()} CZK
                            </span>
                          ) : (
                            <span className="price">{pkg.price?.toLocaleString()} CZK</span>
                          )
                        ) : (
                          <span className="price depends">Depends on project</span>
                        )}
                        <span className="studio">Studio: +{pkg.studioRentalPrice?.toLocaleString()} CZK</span>
                      </div>
                      <div className="features-preview">
                        {pkg.features?.filter(f => f.included).slice(0, 3).map((f, i) => (
                          <span key={i} className="feature-tag"><FiCheck /> {f.text}</span>
                        ))}
                        {pkg.features?.filter(f => f.included).length > 3 && (
                          <span className="more-features">+{pkg.features.filter(f => f.included).length - 3} more</span>
                        )}
                      </div>
                    </div>
                    <div className="package-actions">
                      <button onClick={() => setEditingPackage(pkg)} className="action-btn edit"><FiEdit2 /></button>
                      <button onClick={() => handleDeletePackage(pkg._id)} className="action-btn delete"><FiTrash2 /></button>
                    </div>
                  </div>
                ))}
              </div>
              {sessionPackages.length === 0 && <div className="empty-state"><p>No packages yet. Add your first session package!</p></div>}
            </div>

            {/* Add-ons Section */}
            <div className="booking-section">
              <div className="section-header">
                <h3>✨ Add-on Services</h3>
                <button 
                  className="add-service-btn" 
                  onClick={() => setEditingAddon({ 
                    isNew: true, 
                    name: '', 
                    price: null, 
                    icon: '✨',
                    description: '',
                    order: bookingAddons.length + 1
                  })}
                >
                  <FiPlus /> Add Add-on
                </button>
              </div>
              <div className="addons-list">
                {bookingAddons.sort((a, b) => a.order - b.order).map(addon => (
                  <div key={addon._id} className="addon-item">
                    <div className="addon-icon">{addon.icon}</div>
                    <div className="addon-info">
                      <h4>{addon.name}</h4>
                      <p>{addon.description}</p>
                      <span className="addon-price">+{addon.price?.toLocaleString()} CZK</span>
                    </div>
                    <div className="addon-actions">
                      <button onClick={() => setEditingAddon(addon)} className="action-btn edit"><FiEdit2 /></button>
                      <button onClick={() => handleDeleteAddon(addon._id)} className="action-btn delete"><FiTrash2 /></button>
                    </div>
                  </div>
                ))}
              </div>
              {bookingAddons.length === 0 && <div className="empty-state"><p>No add-ons yet. Add your first add-on service!</p></div>}
            </div>
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div className="availability-tab">
            <h2>Manage Availability</h2>
            <p>Click on dates to block them. Blocked dates will not be available for booking.</p>
            {renderAvailabilityCalendar()}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="bookings-tab">
            <h2>Booking Requests</h2>
            {bookings.length === 0 ? (
              <div className="empty-state"><p>No booking requests yet.</p></div>
            ) : (
              <div className="bookings-list">
                {bookings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(booking => (
                  <div key={booking._id} className={`booking-item ${booking.status}`}>
                    <div className="booking-header">
                      <span className={`status-badge ${booking.status}`}>{booking.status}</span>
                      <span className="booking-date">{new Date(booking.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="booking-details">
                      <div className="detail"><strong>Package:</strong> {booking.packageName || booking.service?.name || 'N/A'}</div>
                      <div className="detail"><strong>Date:</strong> {booking.date} at {booking.time}</div>
                      <div className="detail"><strong>Total:</strong> {(booking.totalPrice || booking.service?.price || 0).toLocaleString()} CZK</div>
                      <div className="detail"><strong>Customer:</strong> {booking.customer?.name}</div>
                      <div className="detail"><strong>Email:</strong> {booking.customer?.email}</div>
                      <div className="detail"><strong>Phone:</strong> {booking.customer?.phone}</div>
                      {booking.studioRental && <div className="detail"><strong>Studio:</strong> Yes (+{booking.studioPrice?.toLocaleString()} CZK)</div>}
                      {booking.addons?.length > 0 && <div className="detail"><strong>Add-ons:</strong> {booking.addons.map(a => a.name).join(', ')}</div>}
                      {booking.customer?.message && <div className="detail" style={{ gridColumn: '1/-1' }}><strong>Message:</strong> {booking.customer.message}</div>}
                    </div>
                    <div className="booking-actions">
                      {booking.status === 'pending' && (
                        <>
                          <button onClick={() => handleUpdateBookingStatus(booking._id, 'confirmed')} className="confirm-btn"><FiCheck /> Confirm</button>
                          <button onClick={() => handleUpdateBookingStatus(booking._id, 'rejected')} className="reject-btn"><FiX /> Reject</button>
                        </>
                      )}
                      <button onClick={() => handleDeleteBooking(booking._id)} className="delete-btn"><FiTrash2 /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Home Content Tab */}
        {activeTab === 'home' && (
          <div className="content-tab">
            <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Home Page Content</h2>
            <div className="content-sections">
              <div className="content-section">
                <h3>Hero Section</h3>
                <button className="add-service-btn" onClick={() => setEditingContent({ section: 'home', field: 'hero', data: homeContent?.hero || {} })}>
                  <FiEdit2 /> Edit Hero
                </button>
              </div>
              <div className="content-section">
                <h3>Introduction</h3>
                <p>Greeting: {homeContent?.intro?.greeting || 'Hello!'}</p>
                <p>Name: {homeContent?.intro?.name || "I'm Marta"}</p>
                <button className="add-service-btn" onClick={() => setEditingContent({ section: 'home', field: 'intro', data: homeContent?.intro || {} })}>
                  <FiEdit2 /> Edit Introduction
                </button>
              </div>
              <div className="content-section">
                <h3>Approach Section</h3>
                <p>Title: {homeContent?.approach?.title || 'Not just a picture, but a mood + story + style'}</p>
                <button className="add-service-btn" onClick={() => setEditingContent({ section: 'home', field: 'approach', data: homeContent?.approach || {} })}>
                  <FiEdit2 /> Edit Approach
                </button>
              </div>
              <div className="content-section">
                <h3>CTA Section</h3>
                <p>Title: {homeContent?.cta?.title || 'Your story begins here'}</p>
                <button className="add-service-btn" onClick={() => setEditingContent({ section: 'home', field: 'cta', data: homeContent?.cta || {} })}>
                  <FiEdit2 /> Edit CTA
                </button>
              </div>
            </div>
          </div>
        )}

        {/* About Content Tab */}
        {activeTab === 'about' && (
          <div className="content-tab">
            <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>About Page Content</h2>
            <div className="about-preview">
              <div className="about-preview-photo">
                {aboutContent?.photo ? (
                  <img src={aboutContent.photo} alt="Profile" />
                ) : (
                  <div className="photo-placeholder">
                    <FiUser size={48} />
                    <span>No photo uploaded</span>
                  </div>
                )}
              </div>
              <div className="about-preview-info">
                <h3>{aboutContent?.name || 'Marta Zboravska'}</h3>
                <p><strong>Intro:</strong> {aboutContent?.intro?.replace(/<[^>]*>/g, '').substring(0, 100) || 'Not set'}...</p>
                <p><strong>Specialization:</strong> {aboutContent?.specialization?.replace(/<[^>]*>/g, '').substring(0, 80) || 'Not set'}...</p>
              </div>
            </div>
            <button className="add-service-btn" style={{ marginTop: '2rem' }} onClick={() => setEditingContent({ section: 'about', data: aboutContent || {} })}>
              <FiEdit2 /> Edit About Page
            </button>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="content-tab">
            <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Site Settings</h2>
            <div className="content-sections">
              <div className="content-section">
                <h3>General Settings</h3>
                <p>Site Name: {settings?.siteName || 'Marta Zboravska Photography'}</p>
                <p>Email: {settings?.email || 'contact@example.com'}</p>
                <p>Phone: {settings?.phone || '+420 123 456 789'}</p>
                <p>Location: {settings?.location || 'Prague, Czech Republic & Italy'}</p>
                <button className="add-service-btn" onClick={() => setEditingContent({ section: 'settings', data: settings || {} })}>
                  <FiEdit2 /> Edit Settings
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Photo/Video Modal */}
      {editingItem && (
        <div className="edit-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setEditingItem(null)}><FiX /></button>
            <h2>{editingItem.isNew ? 'Add' : 'Edit'} {editingItem.type === 'photo' ? 'Photo' : 'Video'}</h2>
            
            {editingItem.type === 'video' && (
              <div className="form-group">
                <label>Video URL</label>
                <input 
                  type="text" 
                  value={editingItem.url || ''} 
                  onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })}
                  placeholder="https://res.cloudinary.com/..."
                />
              </div>
            )}
            
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={editingItem.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
            </div>
            
            <div className="form-group">
              <label>Category</label>
              <select value={editingItem.category || ''} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}>
                {editingItem.type === 'photo' ? (
                  <>
                    <option value="personal">Personal</option>
                    <option value="weddings">Weddings</option>
                    <option value="photo-fashion">Photo Fashion</option>
                    <option value="events">Events</option>
                    <option value="other">Other</option>
                  </>
                ) : (
                  <>
                    <option value="reel">Reel</option>
                    <option value="fashion">Fashion</option>
                    <option value="wedding">Wedding</option>
                    <option value="intro">Intro</option>
                    <option value="other">Other</option>
                  </>
                )}
              </select>
            </div>

            {editingItem.type === 'video' && (
              <>
                <div className="form-group">
                  <label>Poster Image URL (optional)</label>
                  <input 
                    type="text" 
                    value={editingItem.posterUrl || ''} 
                    onChange={(e) => setEditingItem({ ...editingItem, posterUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={editingItem.isReel || false} 
                      onChange={(e) => setEditingItem({ ...editingItem, isReel: e.target.checked })}
                    /> Show on Reels page
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={editingItem.isHeroVideo || false} 
                      onChange={(e) => setEditingItem({ ...editingItem, isHeroVideo: e.target.checked })}
                    /> Use as Hero Video
                  </label>
                </div>
              </>
            )}
            
            {editingItem.type === 'photo' && (
              <>
                <div className="form-group">
                  <label><strong>Display Options</strong></label>
                  <div className="checkbox-group">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={editingItem.isPortfolio !== false} 
                        onChange={(e) => setEditingItem({ ...editingItem, isPortfolio: e.target.checked })}
                      /> Show in Portfolio Grid
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={editingItem.isSlider || false} 
                        onChange={(e) => setEditingItem({ ...editingItem, isSlider: e.target.checked })}
                      /> Show in Portfolio Hero Slider (large carousel at top)
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={editingItem.isHomePage || false} 
                        onChange={(e) => setEditingItem({ ...editingItem, isHomePage: e.target.checked })}
                      /> Show in Home Page "Stories" section
                    </label>
                  </div>
                </div>
                {editingItem.isSlider && (
                  <div className="form-group">
                    <label>Slider Order (lower = first)</label>
                    <input 
                      type="number" 
                      value={editingItem.sliderOrder || 0} 
                      onChange={(e) => setEditingItem({ ...editingItem, sliderOrder: Number(e.target.value) })}
                      min="0"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Focal Point</label>
                <div className="position-picker">
                  <div className="position-preview" onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
                    setEditingItem({ ...editingItem, position: `${x}% ${y}%` });
                  }}>
                    <img src={editingItem.url} alt="Preview" style={{ objectPosition: editingItem.position || 'center center' }} />
                    <div className="position-marker" style={getPositionValues(editingItem.position)}></div>
                  </div>
                  <div className="position-presets">
                    {['left top', 'center top', 'right top', 'left center', 'center center', 'right center', 'left bottom', 'center bottom', 'right bottom'].map(pos => (
                      <button key={pos} type="button" className={editingItem.position === pos ? 'active' : ''} onClick={() => setEditingItem({ ...editingItem, position: pos })}>
                        {pos === 'center center' ? '●' : {'left top': '↖', 'center top': '↑', 'right top': '↗', 'left center': '←', 'right center': '→', 'left bottom': '↙', 'center bottom': '↓', 'right bottom': '↘'}[pos]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              </>
            )}
            
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingItem(null)}>Cancel</button>
              <button className="save-button" onClick={editingItem.isNew && editingItem.type === 'video' ? () => { handleAddVideo(editingItem); setEditingItem(null); } : handleSaveEdit}>
                {editingItem.isNew ? 'Add' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {editingService && (
        <div className="edit-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setEditingService(null)}><FiX /></button>
            <h2>{editingService.isNew ? 'Add Service' : 'Edit Service'}</h2>
            <div className="form-group">
              <label>Service Name</label>
              <input type="text" value={editingService.name || ''} onChange={(e) => setEditingService({ ...editingService, name: e.target.value })} placeholder="e.g. Wedding Photography" />
            </div>
            <div className="form-group">
              <label>Duration</label>
              <input type="text" value={editingService.duration || ''} onChange={(e) => setEditingService({ ...editingService, duration: e.target.value })} placeholder="e.g. 4 hours" />
            </div>
            <div className="form-group">
              <label>Price (€)</label>
              <input type="number" value={editingService.price || 0} onChange={(e) => setEditingService({ ...editingService, price: Number(e.target.value) })} placeholder="e.g. 500" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={editingService.description || ''} onChange={(e) => setEditingService({ ...editingService, description: e.target.value })} rows="3" placeholder="Brief description..." />
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingService(null)}>Cancel</button>
              <button className="save-button" onClick={handleSaveService}>Save Service</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Gallery Item Modal */}
      {editingGalleryItem && (
        <div className="edit-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setEditingGalleryItem(null)}><FiX /></button>
            <h2>{editingGalleryItem.isNew ? 'Add Gallery Item' : 'Edit Gallery Item'}</h2>
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={editingGalleryItem.title || ''} onChange={(e) => setEditingGalleryItem({ ...editingGalleryItem, title: e.target.value })} placeholder="e.g. Wedding Collection" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={editingGalleryItem.category || 'personal'} onChange={(e) => setEditingGalleryItem({ ...editingGalleryItem, category: e.target.value })}>
                <option value="personal">Personal Photoshoot</option>
                <option value="weddings">Weddings</option>
                <option value="photo-fashion">Photo: Fashion/Beauty</option>
                <option value="video-fashion">Video: Fashion/Beauty</option>
                <option value="reels">Reels</option>
                <option value="events">Events</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={editingGalleryItem.description || ''} onChange={(e) => setEditingGalleryItem({ ...editingGalleryItem, description: e.target.value })} rows="3" placeholder="Description of this gallery item..." />
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingGalleryItem(null)}>Cancel</button>
              <button className="save-button" onClick={handleSaveGalleryItem}>Save Gallery Item</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Package Modal */}
      {editingPackage && (
        <div className="edit-modal">
          <div className="modal-content modal-large">
            <button className="modal-close" onClick={() => setEditingPackage(null)}><FiX /></button>
            <h2>{editingPackage.isNew ? 'Add Package' : 'Edit Package'}</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Package Name</label>
                <input 
                  type="text" 
                  value={editingPackage.name || ''} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, name: e.target.value })} 
                  placeholder="e.g. 2 Hour Session" 
                />
              </div>
              <div className="form-group">
                <label>Duration</label>
                <input 
                  type="text" 
                  value={editingPackage.duration || ''} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, duration: e.target.value })} 
                  placeholder="e.g. 2 hours" 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Base Price (CZK)</label>
                <input 
                  type="number" 
                  value={editingPackage.price ?? ''} 
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditingPackage({ ...editingPackage, price: v === '' ? null : Number(v) });
                  }} 
                  placeholder="Leave empty for 'Depends on project'" 
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Discount %</label>
                <input 
                  type="number" 
                  value={editingPackage.discountPercent || 0} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, discountPercent: Number(e.target.value) })} 
                  placeholder="0 = no discount" 
                  min="0"
                  max="100"
                />
                {editingPackage.discountPercent > 0 && editingPackage.price != null && editingPackage.price > 0 && (
                  <small style={{color: '#27ae60', marginTop: '0.5rem', display: 'block'}}>
                    Final price: {Math.round(editingPackage.price * (1 - editingPackage.discountPercent / 100)).toLocaleString()} CZK
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Studio Rental Price (CZK)</label>
                <input 
                  type="number" 
                  value={editingPackage.studioRentalPrice || 0} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, studioRentalPrice: Number(e.target.value) })} 
                  placeholder="e.g. 1500" 
                />
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input 
                  type="number" 
                  value={editingPackage.order || 0} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, order: Number(e.target.value) })} 
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea 
                value={editingPackage.description || ''} 
                onChange={(e) => setEditingPackage({ ...editingPackage, description: e.target.value })} 
                rows="2" 
                placeholder="Brief description of this package..." 
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input 
                  type="checkbox" 
                  checked={editingPackage.recommended || false} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, recommended: e.target.checked })}
                /> 
                Mark as Recommended
              </label>
              <label>
                <input 
                  type="checkbox" 
                  checked={editingPackage.isActive !== false} 
                  onChange={(e) => setEditingPackage({ ...editingPackage, isActive: e.target.checked })}
                /> 
                Active (visible on booking page)
              </label>
            </div>

            <div className="features-section">
              <div className="features-header">
                <label>Features</label>
                <button type="button" className="add-feature-btn" onClick={addFeatureToPackage}>
                  <FiPlus /> Add Feature
                </button>
              </div>
              <div className="features-list-edit">
                {(editingPackage.features || []).map((feature, index) => (
                  <div key={index} className="feature-edit-row">
                    <input 
                      type="text" 
                      value={feature.text} 
                      onChange={(e) => updateFeature(index, 'text', e.target.value)}
                      placeholder="Feature description..."
                    />
                    <label className="feature-included">
                      <input 
                        type="checkbox" 
                        checked={feature.included} 
                        onChange={(e) => updateFeature(index, 'included', e.target.checked)}
                      />
                      Included
                    </label>
                    <button type="button" className="remove-feature-btn" onClick={() => removeFeature(index)}>
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
                {(!editingPackage.features || editingPackage.features.length === 0) && (
                  <p className="no-features">No features added yet. Click "Add Feature" to add package features.</p>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingPackage(null)}>Cancel</button>
              <button className="save-button" onClick={handleSavePackage}>Save Package</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Addon Modal */}
      {editingAddon && (
        <div className="edit-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setEditingAddon(null)}><FiX /></button>
            <h2>{editingAddon.isNew ? 'Add Add-on' : 'Edit Add-on'}</h2>
            
            <div className="form-row">
              <div className="form-group" style={{ flex: '0 0 80px' }}>
                <label>Icon</label>
                <input 
                  type="text" 
                  value={editingAddon.icon || ''} 
                  onChange={(e) => setEditingAddon({ ...editingAddon, icon: e.target.value })} 
                  placeholder="💄"
                  style={{ textAlign: 'center', fontSize: '1.5rem' }}
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input 
                  type="text" 
                  value={editingAddon.name || ''} 
                  onChange={(e) => setEditingAddon({ ...editingAddon, name: e.target.value })} 
                  placeholder="e.g. Professional Makeup Artist" 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price (CZK)</label>
                <input 
                  type="number" 
                  value={editingAddon.price || 0} 
                  onChange={(e) => setEditingAddon({ ...editingAddon, price: Number(e.target.value) })} 
                  placeholder="e.g. 1500" 
                />
              </div>
              <div className="form-group">
                <label>Order</label>
                <input 
                  type="number" 
                  value={editingAddon.order || 0} 
                  onChange={(e) => setEditingAddon({ ...editingAddon, order: Number(e.target.value) })} 
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea 
                value={editingAddon.description || ''} 
                onChange={(e) => setEditingAddon({ ...editingAddon, description: e.target.value })} 
                rows="2" 
                placeholder="Brief description of this add-on..." 
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input 
                  type="checkbox" 
                  checked={editingAddon.isActive !== false} 
                  onChange={(e) => setEditingAddon({ ...editingAddon, isActive: e.target.checked })}
                /> 
                Active (visible on booking page)
              </label>
            </div>

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingAddon(null)}>Cancel</button>
              <button className="save-button" onClick={handleSaveAddon}>Save Add-on</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Content Modal */}
      {editingContent && (
        <div className="edit-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setEditingContent(null)}><FiX /></button>
            <h2>Edit {editingContent.section === 'home' ? `Home - ${editingContent.field}` : editingContent.section === 'about' ? 'About Page' : 'Settings'}</h2>
            
            {editingContent.section === 'settings' && (
              <>
                <div className="form-group">
                  <label>Site Name</label>
                  <input type="text" value={editingContent.data.siteName || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, siteName: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={editingContent.data.email || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, email: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="text" value={editingContent.data.phone || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, phone: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input type="text" value={editingContent.data.location || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, location: e.target.value } })} />
                </div>
              </>
            )}

            {editingContent.section === 'about' && (
              <>
                <div className="form-group">
                  <label>Profile Photo</label>
                  <div className="about-photo-upload">
                    {editingContent.data.photo && (
                      <img src={editingContent.data.photo} alt="Profile" className="about-photo-preview" />
                    )}
                    <label className="upload-button small">
                      <FiUpload /> {editingContent.data.photo ? 'Change Photo' : 'Upload Photo'}
                      <input 
                        type="file" 
                        accept="image/*" 
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploading(true);
                          try {
                            const base64Url = await compressImage(file);
                            const result = await photosAPI.upload(base64Url, {
                              title: 'About Photo',
                              category: 'other',
                              isPortfolio: false
                            });
                            if (result.photo?.url) {
                              setEditingContent({ 
                                ...editingContent, 
                                data: { ...editingContent.data, photo: result.photo.url } 
                              });
                              toast.success('Photo uploaded!');
                            }
                          } catch (err) {
                            console.error('Upload error:', err);
                            toast.error('Failed to upload photo');
                          }
                          setUploading(false);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={editingContent.data.name || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, name: e.target.value } })} placeholder="Marta Zboravska" />
                </div>
                <div className="form-group">
                  <label>Introduction <small style={{color: '#888'}}>(use &lt;strong&gt;...&lt;/strong&gt; for bold/italic)</small></label>
                  <textarea value={editingContent.data.intro || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, intro: e.target.value } })} rows="2" placeholder="I am a <strong>photographer and videographer</strong> working between Prague and Italy." />
                </div>
                <div className="form-group">
                  <label>Specialization</label>
                  <textarea value={editingContent.data.specialization || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, specialization: e.target.value } })} rows="2" placeholder="I specialize in the <strong>premium segment</strong>: wedding photography, portraits..." />
                </div>
                <div className="form-group">
                  <label>Clients</label>
                  <textarea value={editingContent.data.clients || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, clients: e.target.value } })} rows="2" placeholder="I shoot for people who value <strong>aesthetics, atmosphere, and depth</strong>." />
                </div>
                <div className="form-group">
                  <label>Style</label>
                  <textarea value={editingContent.data.style || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, style: e.target.value } })} rows="2" placeholder="My style is <strong>cinematic</strong>, with a touch of <strong>vintage elegance</strong>..." />
                </div>
                <div className="form-group">
                  <label>Philosophy Line 1</label>
                  <textarea value={editingContent.data.philosophy1 || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, philosophy1: e.target.value } })} rows="2" placeholder="Not just a picture, but a <strong>mood + story + style</strong>." />
                </div>
                <div className="form-group">
                  <label>Philosophy Line 2</label>
                  <textarea value={editingContent.data.philosophy2 || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, philosophy2: e.target.value } })} rows="2" placeholder="You are not a technical performer, but a <strong>visual author</strong>..." />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>CTA Heading</label>
                    <input type="text" value={editingContent.data.ctaHeading || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, ctaHeading: e.target.value } })} placeholder="Ready to create something beautiful?" />
                  </div>
                  <div className="form-group">
                    <label>CTA Button Text</label>
                    <input type="text" value={editingContent.data.ctaButton || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, ctaButton: e.target.value } })} placeholder="Get in touch" />
                  </div>
                </div>
              </>
            )}

            {editingContent.section === 'home' && editingContent.field === 'hero' && (
              <div className="form-group">
                <label>Hero Video URL</label>
                <input type="text" value={editingContent.data.videoUrl || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, videoUrl: e.target.value } })} placeholder="https://res.cloudinary.com/..." />
              </div>
            )}

            {editingContent.section === 'home' && editingContent.field === 'intro' && (
              <>
                <div className="form-group">
                  <label>Greeting</label>
                  <input type="text" value={editingContent.data.greeting || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, greeting: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={editingContent.data.name || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, name: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Subtitle</label>
                  <textarea value={editingContent.data.subtitle || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, subtitle: e.target.value } })} rows="2" />
                </div>
              </>
            )}

            {editingContent.section === 'home' && editingContent.field === 'approach' && (
              <>
                <div className="form-group">
                  <label>Label</label>
                  <input type="text" value={editingContent.data.label || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, label: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" value={editingContent.data.title || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, title: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Subtitle</label>
                  <textarea value={editingContent.data.subtitle || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, subtitle: e.target.value } })} rows="2" />
                </div>
                <div className="form-group">
                  <label>Highlight Text</label>
                  <textarea value={editingContent.data.highlight || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, highlight: e.target.value } })} rows="2" />
                </div>
              </>
            )}

            {editingContent.section === 'home' && editingContent.field === 'cta' && (
              <>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" value={editingContent.data.title || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, title: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Button Text</label>
                  <input type="text" value={editingContent.data.buttonText || ''} onChange={(e) => setEditingContent({ ...editingContent, data: { ...editingContent.data, buttonText: e.target.value } })} />
                </div>
              </>
            )}
            
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingContent(null)}>Cancel</button>
              <button className="save-button" onClick={() => {
                if (editingContent.section === 'home') {
                  const updatedHome = { ...homeContent, [editingContent.field]: editingContent.data };
                  handleSaveContent('home', updatedHome);
                } else {
                  handleSaveContent(editingContent.section, editingContent.data);
                }
              }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
